// src/appRWAPromo/components/StreamEventPage.jsx
import React, { useEffect, useMemo, useState, useRef } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";

import {
  Box,
  Typography,
  Chip,
  CircularProgress,
  Button,
  alpha,
   
} from "@mui/material";
import PlayCircleOutlineIcon from "@mui/icons-material/PlayCircleOutline";
import BarChartIcon from "@mui/icons-material/BarChart";
import { getAnalysisProfile } from "./analysisProfiles";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import StreamForecastTerminal from "./StreamSignal/StreamForecastTerminal";








import { doc, getDoc, updateDoc } from "firebase/firestore";
import { db } from "../../components/firebase";

const GLASS = {
  bg: "linear-gradient(135deg, rgba(18,22,28,.94) 0%, rgba(10,13,17,.94) 100%)",
  border: "1px solid rgba(255,255,255,.08)",
  text: "#e5e7eb",
  subtext: "#9ca3af",
};

const chipSx = {
  background: "rgba(15,23,42,.85)",
  color: "#e5e7eb",
  border: "1px solid rgba(148,163,184,.45)",
  borderRadius: 999,
  fontSize: 11,
  height: 24,
  "& .MuiChip-label": { px: 1.1, fontWeight: 900 },
};

const fmtNum = (v) => {
  const x = Number(v || 0);
  if (x >= 1_000_000) return `${(x / 1_000_000).toFixed(1)}M`;
  if (x >= 1_000) return `${(x / 1_000).toFixed(1)}K`;
  return String(x);
};

const fmtDateShort = (v) => {
  if (!v) return "";
  const d = typeof v === "number" ? new Date(v) : new Date(String(v));
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString(undefined, {
    day: "2-digit",
    month: "short",
  });
};

const fmtTimeLeft = (deadline) => {
  if (!deadline) return "";
  const now = Date.now();
  const ts = typeof deadline === "number" ? deadline : new Date(deadline).getTime();
  if (!Number.isFinite(ts)) return "";
  const diffMs = ts - now;
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  if (diffMs <= 0) return "The event has ended.";
  if (diffDays <= 0) return "Less than a day";
  return `~${diffDays}d before the deadline`;
};

function LiveVideo({ src, poster, autoplay = true, loop = true }) {
  const ref = useRef(null);
  const [muted, setMuted] = useState(true);

  useEffect(() => {
    const v = ref.current;
    if (!v) return;
    if (autoplay) v.play().catch(() => {});
  }, [src, autoplay]);

  return (
    <Box sx={{ position: "absolute", inset: 0 }}>
      <video
        ref={ref}
        src={src}
        poster={poster || undefined}
        autoPlay={autoplay}
        loop={loop}
        playsInline
        muted={muted}
        controls={false}
        preload="auto"
        disablePictureInPicture
        controlsList="nodownload noplaybackrate noremoteplayback"
        onPause={() => ref.current?.play().catch(() => {})}
        style={{
          position: "absolute",
          inset: 0,
          width: "100%",
          height: "100%",
          objectFit: "cover",
          display: "block",
          pointerEvents: "none", // нельзя паузить кликом
          background: "transparent",
        }}
      />

      <Box sx={{ position: "absolute", right: 10, bottom: 10, zIndex: 5, pointerEvents: "auto" }}>
        <Button
          variant="contained"
          onClick={() => {
            setMuted((m) => {
              const next = !m;
              const v = ref.current;
              if (v) v.muted = next;
              if (v && !next) v.volume = 1;
              return next;
            });
          }}
          sx={{ borderRadius: 999, fontWeight: 900, textTransform: "none" }}
        >
          {muted ? "Sound off" : "Sound on"}
        </Button>
      </Box>
    </Box>
  );
}
const STORAGE_KEY_PREFIX = "rwa_stream_votes_";



export default function StreamEventPage() {
  const { slugAndId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [item, setItem] = useState(() => location.state?.item || null);

  const [predictions, setPredictions] = useState([]);
  const [localVotes, setLocalVotes] = useState({});
  const [loading, setLoading] = useState(() => !location.state?.item);

  const [savingVote, setSavingVote] = useState(false);
  const [error, setError] = useState(null);
  
  
  const [getOpen, setGetOpen] = useState(false);

  const id = useMemo(() => {
    if (!slugAndId) return null;
    const parts = String(slugAndId).split("-");
    return parts[parts.length - 1] || null;
  }, [slugAndId]);

  const hasInitialRef = useRef(Boolean(location.state?.item));

useEffect(() => {
  if (!id) return;
  let cancelled = false;

  async function load() {
    if (!hasInitialRef.current) setLoading(true);
    setError(null);

    try {
      const refDoc = doc(db, "avatar_streams", id);
      const snap = await getDoc(refDoc);
      if (!snap.exists()) throw new Error("Stream event not found");
      if (cancelled) return;

      const data = snap.data() || {};
      setItem({ id: snap.id, type: "stream", ...data });

      const preds = Array.isArray(data.predictions) ? data.predictions : [];
        setPredictions(
          preds.map((p, idx) => ({
            id: p.id || String(idx),
            question: p.question || p.title || "Prediction",
            asset: p.asset || "",
            deadline: p.deadline || null,
            probability: typeof p.probability === "number" ? p.probability : null,
            yesCount: Number(p.yesCount || 0),
            noCount: Number(p.noCount || 0),
            status: p.status || "open", // open | closed | settled
            outcome: p.outcome || null, // yes | no | null
          }))
        );
      } catch (e) {
        if (!cancelled) setError(e);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    load();
    return () => {
      cancelled = true;
    };
  }, [id, db]);

  // local votes
  useEffect(() => {
    if (!id) return;
    try {
      const raw = window.localStorage.getItem(`${STORAGE_KEY_PREFIX}${id}`);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (parsed && typeof parsed === "object") {
          setLocalVotes(parsed);
        }
      }
    } catch {
      // ignore
    }
  }, [id]);

  const persistVotes = (next) => {
    if (!id) return;
    setLocalVotes(next);
    try {
      window.localStorage.setItem(`${STORAGE_KEY_PREFIX}${id}`, JSON.stringify(next));
    } catch {
      // ignore
    }
  };

  const handleVote = async (predictionId, side) => {
    if (!id || !predictionId || !side) return;
    if (savingVote) return;

    const prevSide = localVotes[predictionId];
    if (prevSide === side) {
      return;
    }

    let updatedPreds = [];
    setPredictions((prev) => {
      updatedPreds = prev.map((p) => {
        if (p.id !== predictionId) return p;

        let yes = Number(p.yesCount || 0);
        let no = Number(p.noCount || 0);

        if (prevSide === "yes") yes = Math.max(0, yes - 1);
        if (prevSide === "no") no = Math.max(0, no - 1);

        if (side === "yes") yes += 1;
        if (side === "no") no += 1;

        return {
          ...p,
          yesCount: yes,
          noCount: no,
        };
      });
      return updatedPreds;
    });

    const nextVotes = { ...localVotes, [predictionId]: side };
    persistVotes(nextVotes);

    try {
      setSavingVote(true);
      const ref = doc(db, "avatar_streams", id);
      await updateDoc(ref, {
        predictions: updatedPreds,
      });
    } catch (e) {
      console.error("Failed to save vote", e);
    } finally {
      setSavingVote(false);
    }
  };

 const handleOpenPersonalForecast = () => {
  setGetOpen((v) => !v);
};



  if (!id) {
    return (
      <Box sx={{ p: 3, color: GLASS.text }}>
        <Typography variant="h6">Invalid stream URL</Typography>
      </Box>
    );
  }

  if (loading && !item) {
  return (
    <Box sx={{ p: 4, display: "flex", justifyContent: "center", alignItems: "center" }}>
      <CircularProgress />
    </Box>
  );
}

  if (error || !item) {
    return (
      <Box sx={{ p: 3, color: GLASS.text }}>
        <Typography variant="h6" sx={{ mb: 1 }}>
          Stream not found
        </Typography>
        <Typography sx={{ color: GLASS.subtext, fontSize: 14 }}>
          {error?.message || "This stream event does not exist or was removed."}
        </Typography>
      </Box>
    );
  }

  const title = item.name || item.title || "Stream";
  const subtitle = item.handle || item.subtitle || "";
  const category = item?.category || item?.market || "Crypto";
  const profile = getAnalysisProfile(category);

  const signals = Array.isArray(item.aiSignals) ? item.aiSignals : [];

  return (
    <Box
      sx={{
        minHeight: "100vh",
        bgcolor: "#020617",
        color: GLASS.text,
        py: 3,
        px: { xs: 1.5, sm: 2.5, md: 4 },
      }}
    >
      {/* HEADER */}
      <Box sx={{ maxWidth: 1200, mx: "auto", mb: 2 }}>
         <Button
        onClick={() => {
          if (window.history.length > 1) {
            navigate(-1);
          } else {
            navigate("/market");
          }
        }}
        variant="text"
        size="small"
        sx={{ /* стили как у тебя */ }}
      >
        ← Back to Market
      </Button>

        <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap", mb: 1 }}>
          <Chip label="STREAM EVENT" sx={chipSx} />
          {item.status ? <Chip label={String(item.status).toUpperCase()} sx={chipSx} /> : null}
          {category ? <Chip label={category} sx={chipSx} /> : null}
        </Box>

        <Typography
          variant="h4"
          sx={{
            fontWeight: 900,
            letterSpacing: "-.03em",
            mb: 0.5,
          }}
        >
          {title}
        </Typography>
        {subtitle && (
          <Typography sx={{ color: GLASS.subtext, fontSize: 14, mb: 1 }}>
            {subtitle}
          </Typography>
        )}

        <Typography sx={{ color: GLASS.subtext, fontSize: 13 }}>
          Views: <b>{fmtNum(item.views)}</b>
        </Typography>
      </Box>

      {/* MAIN GRID */}
      <Box
        sx={{
          maxWidth: 1200,
          mx: "auto",
          display: "grid",
          gridTemplateColumns: { md: "minmax(0,2fr) minmax(0,1.35fr)" },
          gap: 3,
          alignItems: "flex-start",
        }}
      >
        {/* LEFT COLUMN: predictions + signals */}
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <PredictionPanel
            predictions={predictions}
            localVotes={localVotes}
            onVote={handleVote}
            savingVote={savingVote}
          />

          <SignalsHistoryPanel signals={signals} />
        </Box>

        {/* RIGHT COLUMN: video + CTA + description */}
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          {/* VIDEO */}
          <Box
            sx={{
              position: "relative",
              width: "100%",
              paddingTop: "56.25%",
              borderRadius: 3,
              overflow: "hidden",
              border: GLASS.border,
              boxShadow: "0 12px 40px rgba(15,23,42,.7)",
              background: "rgba(15,23,42,.9)",
            }}
          >
            {item.videoSrc ? (
  <LiveVideo
    src={item.videoSrc}
    poster={item.poster}
    autoplay={true}
    loop={Boolean(item.loop ?? true)}
  />
            ) : (
              <Box
                sx={{
                  position: "absolute",
                  inset: 0,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  bgcolor: "rgba(15,23,42,.9)",
                }}
              >
                <PlayCircleOutlineIcon sx={{ fontSize: 56, color: "#64748b" }} />
              </Box>
            )}
          </Box>

      {profile.engine === "crypto" ? (
  <StreamForecastTerminal
    streamItem={item}
    defaultSymbol={item?.symbol || item?.asset || "BTC"}
    defaultTf={profile.defaultTf}
    analysisType={profile.callableType}     // важно: для "signal" используем callableType профиля
    allowedTypes={profile.allowedTypes}     // ограничиваем меню
    category={category}                      // в context на бэк
  />
) : (
  <Box
    sx={{
      borderRadius: 3,
      border: GLASS.border,
      p: 2,
      background: "rgba(15,23,42,.9)",
    }}
  >
    <Typography sx={{ fontWeight: 800, fontSize: 14 }}>
      Analysis is not available for {category}
    </Typography>
    <Typography sx={{ color: GLASS.subtext, fontSize: 13 }}>
      This category uses a different analysis engine and will be enabled separately.
    </Typography>
  </Box>
)}


      
 

       {/* DESCRIPTION */}
          <Box
            sx={{
              borderRadius: 3,
              border: GLASS.border,
              p: 2,
              background: "linear-gradient(135deg, rgba(15,23,42,.88), rgba(15,23,42,.96))",
            }}
          >
            <Typography sx={{ fontWeight: 800, mb: 1, fontSize: 14 }}>
              Stream description
            </Typography>
            <Typography sx={{ color: GLASS.subtext, fontSize: 13, whiteSpace: "pre-line" }}>
              {item.description || "No description yet."}
            </Typography>
          </Box>
        </Box> 
      </Box> 
    </Box> 
  );
}
/* ================== LEFT: Prediction panel ================== */

function PredictionPanel({ predictions, localVotes, onVote, savingVote }) {
  const hasPreds = Array.isArray(predictions) && predictions.length > 0;

  const fallback = [
    {
      id: "demo-btc-200k",
      question: "Bitcoin will rise to $200,000 by the end of December.?",
      asset: "BTC",
      deadline: new Date(new Date().getFullYear(), 11, 31).getTime(),
      probability: 0.32,
      yesCount: 128,
      noCount: 342,
      status: "open",
    },
    {
      id: "demo-eth-etf",
      question: "Will a US ETH spot ETF launch by the end of the year?",
      asset: "ETH",
      deadline: new Date(new Date().getFullYear(), 11, 31).getTime(),
      probability: 0.58,
      yesCount: 412,
      noCount: 295,
      status: "open",
    },
    {
      id: "demo-sol",
      question: "Will SOL stay above the top 3 on mcap during the BTC halving?",
      asset: "SOL",
      deadline: null,
      probability: 0.47,
      yesCount: 203,
      noCount: 187,
      status: "open",
    },
  ];

  const list = hasPreds ? predictions : fallback;

  return (
    <Box
      sx={{
        borderRadius: 3,
        border: GLASS.border,
        p: 1.8,
        background: "linear-gradient(135deg, rgba(15,23,42,.96), rgba(8,47,73,.94))",
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", mb: 1.5, gap: 1 }}>
        <Box
          sx={{
            width: 28,
            height: 28,
            borderRadius: 999,
            bgcolor: "rgba(59,130,246,.18)",
            border: "1px solid rgba(59,130,246,.6)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <BarChartIcon sx={{ fontSize: 18, color: "#bfdbfe" }} />
        </Box>
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ fontWeight: 900, fontSize: 14, letterSpacing: ".02em", textTransform: "uppercase" }}>
            Prediction board
          </Typography>
          <Typography sx={{ color: GLASS.subtext, fontSize: 12 }}>
            Voting on stream events: yes/no, to gauge audience sentiment.
          </Typography>
        </Box>
      </Box>

      <Box sx={{ display: "flex", flexDirection: "column", gap: 1.1, mt: 1 }}>
        {list.slice(0, 10).map((p) => (
          <PredictionRow
            key={p.id}
            prediction={p}
            myVote={localVotes?.[p.id]}
            onVote={onVote}
            saving={savingVote}
          />
        ))}
      </Box>
    </Box>
  );
}

function PredictionRow({ prediction, myVote, onVote, saving }) {
  const total = (Number(prediction.yesCount) || 0) + (Number(prediction.noCount) || 0);
  const yesPct = total ? Math.round((Number(prediction.yesCount || 0) / total) * 100) : 0;
  const noPct = total ? 100 - yesPct : 0;

  const isClosed = prediction.status === "closed" || prediction.status === "settled";
  const timeLeft = fmtTimeLeft(prediction.deadline);
  const deadlineCompact = fmtDateShort(prediction.deadline);

  const prob = typeof prediction.probability === "number" ? Math.round(prediction.probability * 100) : null;

  const handleYes = () => {
    if (isClosed || saving) return;
    onVote?.(prediction.id, "yes");
  };
  const handleNo = () => {
    if (isClosed || saving) return;
    onVote?.(prediction.id, "no");
  };

  const yesActive = myVote === "yes";
  const noActive = myVote === "no";

  return (
    <Box
      sx={{
        borderRadius: 2,
        border: "1px solid rgba(148,163,184,.45)",
        bgcolor: "rgba(15,23,42,.9)",
        p: 1.1,
      }}
    >
      <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ fontSize: 13, fontWeight: 700, color: "#f9fafb" }}>
            {prediction.question}
          </Typography>

          <Box sx={{ mt: 0.4, display: "flex", flexWrap: "wrap", gap: 0.7, alignItems: "center" }}>
            {prediction.asset ? (
              <Chip
                size="small"
                label={String(prediction.asset).toUpperCase()}
                sx={{
                  height: 20,
                  borderRadius: 999,
                  border: "1px solid rgba(148,163,184,.6)",
                  bgcolor: "rgba(15,23,42,.9)",
                  "& .MuiChip-label": { fontSize: 11, fontWeight: 800, px: 0.8 },
                }}
              />
            ) : null}

            {prob != null && (
              <Chip
                size="small"
                label={`AI prob: ${prob}%`}
                sx={{
                  height: 20,
                  borderRadius: 999,
                  border: "1px solid rgba(96,165,250,.7)",
                  bgcolor: "rgba(37,99,235,.15)",
                  color: "#bfdbfe",
                  "& .MuiChip-label": { fontSize: 11, fontWeight: 800, px: 0.8 },
                }}
              />
            )}

            {deadlineCompact && (
              <Typography sx={{ fontSize: 11, color: GLASS.subtext }}>
                to {deadlineCompact}
              </Typography>
            )}

            {timeLeft && (
              <Typography sx={{ fontSize: 11, color: alpha("#fbbf24", 0.9) }}>
                {timeLeft}
              </Typography>
            )}

            {prediction.status === "settled" && prediction.outcome && (
              <Chip
                size="small"
                icon={<CheckCircleIcon sx={{ fontSize: 14 }} />}
                label={`Результат: ${prediction.outcome === "yes" ? "Да" : "Нет"}`}
                sx={{
                  height: 20,
                  borderRadius: 999,
                  border: "1px solid rgba(34,197,94,.6)",
                  bgcolor: "rgba(22,163,74,.18)",
                  color: "#bbf7d0",
                  "& .MuiChip-label": { fontSize: 11, fontWeight: 800, px: 0.6 },
                }}
              />
            )}
          </Box>
        </Box>
      </Box>

      {/* Crowd bar */}
      <Box sx={{ mt: 0.65 }}>
        <Box
          sx={{
            position: "relative",
            height: 6,
            borderRadius: 999,
            overflow: "hidden",
            bgcolor: "rgba(15,23,42,1)",
          }}
        >
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              width: `${yesPct}%`,
              bgcolor: "rgba(34,197,94,.85)",
              transition: "width .25s ease-out",
            }}
          />
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              ml: "auto",
              width: `${noPct}%`,
              bgcolor: "rgba(239,68,68,.8)",
              transition: "width .25s ease-out",
            }}
          />
        </Box>
        <Box
          sx={{
            mt: 0.45,
            display: "flex",
            justifyContent: "space-between",
            fontSize: 11,
            color: GLASS.subtext,
          }}
        >
          <span>
            Yes:{" "}
            <b style={{ color: "#bbf7d0" }}>
              {yesPct}% ({prediction.yesCount || 0})
            </b>
          </span>
          <span>
            No:{" "}
            <b style={{ color: "#fecaca" }}>
              {noPct}% ({prediction.noCount || 0})
            </b>
          </span>
        </Box>
      </Box>

      {/* Vote buttons */}
      <Box
        sx={{
          mt: 0.7,
          display: "flex",
          gap: 0.8,
          justifyContent: "flex-end",
          flexWrap: "wrap",
        }}
      >
        <Button
          size="small"
          variant={yesActive ? "contained" : "outlined"}
          onClick={handleYes}
          disabled={isClosed || saving}
          sx={{
            textTransform: "none",
            fontWeight: 800,
            fontSize: 12,
            borderRadius: 999,
            px: 1.8,
            height: 28,
            borderColor: yesActive ? "rgba(34,197,94,.9)" : "rgba(34,197,94,.5)",
            bgcolor: yesActive ? "rgba(34,197,94,.95)" : "transparent",
            color: yesActive ? "#022c22" : "#bbf7d0",
            "&:hover": {
              borderColor: "rgba(34,197,94,1)",
              bgcolor: yesActive ? "rgba(22,163,74,1)" : "rgba(22,163,74,.18)",
            },
          }}
        >
          Yes
        </Button>
        <Button
          size="small"
          variant={noActive ? "contained" : "outlined"}
          onClick={handleNo}
          disabled={isClosed || saving}
          sx={{
            textTransform: "none",
            fontWeight: 800,
            fontSize: 12,
            borderRadius: 999,
            px: 1.8,
            height: 28,
            borderColor: noActive ? "rgba(239,68,68,.9)" : "rgba(239,68,68,.6)",
            bgcolor: noActive ? "rgba(239,68,68,.95)" : "transparent",
            color: noActive ? "#7f1d1d" : "#fecaca",
            "&:hover": {
              borderColor: "rgba(239,68,68,1)",
              bgcolor: noActive ? "rgba(220,38,38,1)" : "rgba(220,38,38,.16)",
            },
          }}
        >
          No
        </Button>
      </Box>

       
    </Box>
  );
}

/* ================== LEFT: Signals history ================== */

function SignalsHistoryPanel({ signals }) {
  const hasSignals = Array.isArray(signals) && signals.length > 0;

  const fallback = [
    {
      id: "sig-btc",
      symbol: "BTC",
      dir: "long",
      entry: 62000,
      exit: 65400,
      pnlPct: 5.5,
      tf: "4h",
      label: "Range Breakout + Volume",
    },
    {
      id: "sig-sol",
      symbol: "SOL",
      dir: "short",
      entry: 195,
      exit: 180,
      pnlPct: 7.7,
      tf: "1h",
      label: "Overheating funding + OI",
    },
    {
      id: "sig-eth",
      symbol: "ETH",
      dir: "long",
      entry: 3200,
      exit: 3425,
      pnlPct: 7.0,
      tf: "1d",
      label: "Squeeze to Resistance + Long Wall",
    },
  ];

  const list = hasSignals ? signals : fallback;

  return (
    <Box
      sx={{
        borderRadius: 3,
        border: GLASS.border,
        p: 1.8,
        background: "linear-gradient(135deg, rgba(15,23,42,.96), rgba(30,64,175,.9))",
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
        <Box
          sx={{
            width: 26,
            height: 26,
            borderRadius: 999,
            bgcolor: "rgba(52,211,153,.18)",
            border: "1px solid rgba(16,185,129,.7)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <CheckCircleIcon sx={{ fontSize: 17, color: "#bbf7d0" }} />
        </Box>
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ fontWeight: 900, fontSize: 13, letterSpacing: ".04em", textTransform: "uppercase" }}>
            AI signals that played out
          </Typography>
          <Typography sx={{ color: GLASS.subtext, fontSize: 12 }}>
            Examples of trades based on AI signals that have already worked (demo mode).
          </Typography>
        </Box>
      </Box>

      <Box sx={{ display: "flex", flexDirection: "column", gap: 0.8, mt: 0.5 }}>
        {list.slice(0, 5).map((s) => (
          <Box
            key={s.id || `${s.symbol}-${s.entry}-${s.exit}`}
            sx={{
              borderRadius: 2,
              border: "1px solid rgba(148,163,184,.4)",
              bgcolor: "rgba(15,23,42,.92)",
              p: 1,
              display: "flex",
              flexDirection: "column",
              gap: 0.25,
            }}
          >
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 1 }}>
              <Typography sx={{ fontSize: 12, fontWeight: 800, color: "#f9fafb" }}>
                {String(s.symbol || "").toUpperCase()}/USDT · {s.tf || "—"}
              </Typography>
              <Chip
                size="small"
                label={s.dir === "short" ? "SHORT" : "LONG"}
                sx={{
                  height: 20,
                  borderRadius: 999,
                  border:
                    s.dir === "short"
                      ? "1px solid rgba(248,113,113,.8)"
                      : "1px solid rgba(34,197,94,.8)",
                  bgcolor:
                    s.dir === "short"
                      ? "rgba(239,68,68,.18)"
                      : "rgba(34,197,94,.2)",
                  color: "#f9fafb",
                  "& .MuiChip-label": {
                    fontSize: 10,
                    fontWeight: 900,
                    px: 0.8,
                    letterSpacing: ".08em",
                  },
                }}
              />
            </Box>
            <Typography sx={{ fontSize: 11.5, color: "#cbd5e1" }}>
              Entrance: <b>{s.entry}</b> · Exit: <b>{s.exit}</b>{" "}
              · PnL:{" "}
              <b style={{ color: s.pnlPct >= 0 ? "#bbf7d0" : "#fecaca" }}>
                {s.pnlPct >= 0 ? "+" : ""}
                {s.pnlPct}%
              </b>
            </Typography>
            {s.label && (
              <Typography sx={{ fontSize: 11, color: "#94a3b8" }}>
                {s.label}
              </Typography>
            )}
          </Box>
        ))}


   

      </Box>
  


      <Typography
        sx={{
          mt: 1.1,
          fontSize: 11,
          color: alpha(GLASS.subtext, 0.9),
        }}
      >
        The history shown is for illustrative purposes only and does not guarantee future results. This is not investment advice.
      </Typography>

    </Box>
  );
}
